import React, { useState } from "react";
import { Icon } from "@iconify/react";
import axios from "axios";
import { useSelector } from "react-redux";
import GameCard from "./GameCard";
import { buildGameStartPayload } from "../utils/gameStartPayload";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const remoteProviderPrefixes = [
  "bs",
  "ng",
  "aws",
  "swg",
  "km",
  "fc",
  "mg",
  "dgs",
  "gamatron",
  "ep",
  "jl",
  "sn",
  "pp",
  "kg",
  "netent",
  "joker",
  "bpg",
  "bng",
  "hab",
  "kgl",
  "rlx",
  "ygg",
  "red",
  "qs",
  "ids",
  "tk",
  "ds",
  "nlc",
  "ga",
  "png",
  "pug",
  "fng",
  "nge",
  "1x2",
  "hak",
  "waz",
  "elk",
  "prs",
  "dgs",
];

const HomeCasinoSection = ({ gameList, loading, error, onProviderClick }) => {
  // New state to support provider click-through like LiveCasino
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerGames, setProviderGames] = useState([]);
  const [isLoadingGames, setIsLoadingGames] = useState(false);
  const [gamesError, setGamesError] = useState(null);
  const [gameStartLoading, setGameStartLoading] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "warning",
  });

  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "warning" });
  };

  const loadProviderGames = async (provider) => {
    const providerCode = (
      provider?.game ||
      provider?.productId ||
      ""
    ).toLowerCase();
    const providerName = (
      provider?.name ||
      provider?.productName ||
      ""
    ).toLowerCase();
    const shouldUseRemote =
      remoteProviderPrefixes.includes(providerCode) ||
      remoteProviderPrefixes.includes(providerName);

    if (!shouldUseRemote) {
      setSelectedProvider(null);
      setIsLoadingGames(false);
      setGamesError(null);
      setProviderGames([]);
      return getGameLink(provider);
    }

    setSelectedProvider(provider);
    setIsLoadingGames(true);
    setGamesError(null);
    setProviderGames([]);

    try {
      const res = await axios({
        method: "GET",
        url: `games/${providerCode}.json`,
        responseType: "json",
        maxBodyLength: Infinity,
      });
      const payload = res?.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      if (Array.isArray(list)) {
        setProviderGames(list);
        setIsLoadingGames(false);
        return;
      }
    } catch (err) {
      console.warn(
        "Failed to load games from list source, fallback to API",
        err,
      );
    }

    const params = {
      providerPrefix: providerCode,
      categories: "",
      page: 1,
      perPage: 40,
    };

    try {
      const res = await axios({
        method: "GET",
        url: `${API_BASE_URL}/gamelogin/getgamelist`,
        params,
        maxBodyLength: Infinity,
      });
      const list = res?.data?.gamesList
        ? res.data.gamesList
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data?.games)
            ? res.data.games
            : [];
      setProviderGames(list || []);
    } catch (err) {
      console.error(err);
      setGamesError("ไม่สามารถโหลดรายการเกมได้");
      setProviderGames([]);
    } finally {
      setIsLoadingGames(false);
    }
  };

  const backToProviders = () => {
    setSelectedProvider(null);
    setProviderGames([]);
    setGamesError(null);
  };

  const playerBalance = useSelector((state) => state.user.playerBalance);

  const getGameLink = async (request) => {
    setGameStartLoading(true);
    let userId = null;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        userId = parsed?.id || parsed?.user?.id || parsed?.UserID || null;
      }
    } catch {}

    const data = { id: userId };

    if (selectedProvider) {
      data.provider =
        selectedProvider?.productId ||
        selectedProvider?.game ||
        selectedProvider?.name;
      data.gamecode =
        request?.code ||
        request?.gameCode ||
        request?.gamecode ||
        request?.game;
    } else {
      const fallbackProvider =
        request?.productId ||
        request?.game ||
        request?.provider ||
        request?.name;
      data.provider = fallbackProvider;
      data.gamecode =
        request?.code ||
        request?.gameCode ||
        request?.gamecode ||
        fallbackProvider;
    }

    const gameWindow = window.open("", "_blank");
    try {
      const res = await axios({
        method: "POST",
        url: `${API_BASE_URL}/gamelogin/gamestart`,
        data: {
          data: buildGameStartPayload({ data, playerBalance }),
        },
        maxBodyLength: Infinity,
      });
      if (res.data) {
        if (gameWindow) gameWindow.location.href = res.data;
        else window.location.href = res.data;
      }
    } catch (err) {
      console.error(err);
      if (gameWindow) gameWindow.close();
      alert("เกิดข้อผิดพลาดในการเข้าสู่เกมส์");
    } finally {
      setGameStartLoading(false);
    }
  };

  // Permission helpers using redux state
  const { singleUsers } = useSelector((state) => state.user);
  const currentUser = singleUsers && singleUsers.length ? singleUsers[0] : null;
  const providerAllowanceRaw =
    currentUser?.gameProviderAvailable || "ALLPROVIDER";
  const providerAllowanceList =
    providerAllowanceRaw === "ALLPROVIDER"
      ? null
      : providerAllowanceRaw.split(",").map((s) => s.trim().toLowerCase());
  const typeAllowanceList = currentUser?.gameTypeAvailable
    ? currentUser.gameTypeAvailable
        .split(",")
        .map((s) => s.trim().toLowerCase())
    : ["allgametype"];

  const isTypeAllowedFor = (type) => {
    const map = {
      slot: "slots",
      casino: "livecasino",
      fishing: "fishing",
      sport: "sportbook",
    };
    const key = map[type] || type;
    return (
      typeAllowanceList.includes("allgametype") ||
      typeAllowanceList.includes(key) ||
      typeAllowanceList.includes(type)
    );
  };

  const isProviderAllowed = (prov) => {
    if (!currentUser) return true;
    if (providerAllowanceList === null) return true;
    const candidates = [];
    if (typeof prov === "string") candidates.push(prov);
    else candidates.push(prov.productId, prov.productName, prov.name, prov.id);
    const normalized = candidates
      .filter((c) => c !== undefined && c !== null && c !== "")
      .map((c) => String(c).toLowerCase());
    return normalized.some((n) => providerAllowanceList.includes(n));
  };

  const handleProviderSelect = (provider) => {
    if (!currentUser) {
      showToast("กรุณาเข้าสู่ระบบก่อนเข้าเล่น", "info");
      return;
    }

    if (!isTypeAllowedFor("casino")) {
      showToast(
        "ท่านไม่สามารถเล่นประเภทเกมส์คาสิโนนี้ได้ เนื่องจากติดเงื่อนไข",
        "warning",
      );
      return;
    }
    if (!isProviderAllowed(provider)) {
      showToast(
        "ท่านสามารถเล่นได้บางประเภท,บางค่ายเกมส์เท่านั้น เนื่องจากติดเงื่อนไข",
        "warning",
      );
      return;
    }
    loadProviderGames(provider);
  };

  return (
    <div style={{ marginTop: 40 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <h1
          style={{
            color: "#0e6c5e",
            margin: 0,
            fontSize: 32,
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Icon
            icon="cil:casino"
            width={32}
            height={32}
            style={{ color: "#0e6c5e" }}
          />
          {selectedProvider ? "เกมจากผู้ให้บริการ" : "คาสิโน"}
        </h1>
        {selectedProvider && (
          <button
            onClick={backToProviders}
            style={{
              background: "color-mix(in srgb, #0e6c5e 10%, transparent)",
              border: "2px solid #0e6c5e",
              color: "#0e6c5e",
              borderRadius: 25,
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: 700,
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#0e6c5e";
              e.currentTarget.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background =
                "color-mix(in srgb, #0e6c5e 10%, transparent)";
              e.currentTarget.style.color = "#0e6c5e";
            }}
          >
            กลับไปหน้าผู้ให้บริการ
          </button>
        )}
      </div>
      <hr style={{ border: "2px solid #b7e3d8", marginBottom: 20 }} />

      {!selectedProvider ? (
        <div
          className="casino-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: 16,
          }}
        >
          {loading && <p>Loading games...</p>}
          {error && <p style={{ color: "red" }}>Error: {error}</p>}

          {gameList?.liveCasino?.slice(0, 9).map((provider, index) => {
            const img = `https://space.kuga59.co/menu-image/${provider?.game}.webp`;
            const src = img.startsWith("http")
              ? img
              : `https://space.kuga59.co/${img}`;
            return (
              <div
                key={provider.productId || provider.game}
                style={{
                  backgroundColor: "#2d2d2d",
                  borderRadius: 12,
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  border: "1px solid #404040",
                  gridColumn: index === 0 ? "span 2" : "auto",
                  gridRow: index === 0 ? "span 2" : "auto",
                  position: "relative",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.05)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 25px var(--primaryColor,--primaryColor)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "none";
                }}
                onClick={() => handleProviderSelect(provider)}
              >
                <img
                  src={src}
                  alt={provider.productName || provider.name}
                  style={{
                    width: "100%",
                    objectFit: "contain",
                    borderRadius: 8,
                    height: index === 0 ? "100%" : "auto",
                  }}
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
                <h3
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    color: "white",
                    fontSize: 14,
                    margin: 0,
                    padding: "4px 12px",
                    fontWeight: 600,
                    backgroundColor: "rgba(0,0,0,0.7)",
                    borderRadius: "0 0 8px 8px",
                  }}
                >
                  {provider.productName || provider.name}
                </h3>
              </div>
            );
          })}
        </div>
      ) : (
        <div>
          <div style={{ color: "#000000", marginBottom: 12 }}>
            ผู้ให้บริการ:{" "}
            {selectedProvider?.productName || selectedProvider?.name || "-"}
          </div>
          {isLoadingGames && (
            <div style={{ color: "#aaa" }}>กำลังโหลดรายการเกม...</div>
          )}
          {gamesError && <div style={{ color: "#ef4444" }}>{gamesError}</div>}
          {!isLoadingGames && !gamesError && (
            <div
              className="casino-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(6, 1fr)",
                gap: 16,
              }}
            >
              {providerGames?.length ? (
                providerGames.map((g) => (
                  <GameCard
                    key={
                      g.gameId ||
                      g.id ||
                      `${g.providerId || selectedProvider?.productId}-${
                        g.code || g.gameCode || g.gamecode || g.name
                      }`
                    }
                    game={g}
                    onClick={() => getGameLink(g)}
                  />
                ))
              ) : (
                <div style={{ color: "#aaa" }}>
                  ไม่พบเกมสำหรับผู้ให้บริการนี้
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {gameStartLoading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          กำลังเริ่มเกม...
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            backgroundColor: "#FEF3C7",
            border: "2px solid #F59E0B",
            borderRadius: "12px",
            padding: "16px 20px",
            maxWidth: "400px",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <span style={{ fontSize: "20px" }}>⚠️</span>
          <span style={{ color: "#92400E", fontWeight: "500", flex: 1 }}>
            {toast.message}
          </span>
          <button
            onClick={hideToast}
            style={{
              background: "none",
              border: "none",
              fontSize: "18px",
              cursor: "pointer",
              color: "#92400E",
              padding: "0",
              marginLeft: "8px",
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default HomeCasinoSection;
